// Wizard-state container for the new submission-first signup flow.
// Survives refresh via sessionStorage (per-tab, like the legacy Session_Id):
// each browser tab is an independent flow by design.

import { createModel } from "@rematch/core";
import * as Sentry from "@sentry/react";
import {
  getForcedCountryCode,
  getSubmissionFlowApi,
  IBreakdownSubmissionInfoDto,
  ISubmissionGeoGuessDto,
} from "../shared/api/submission";
import {
  readCachedGeo,
  writeCachedGeo,
} from "../components/Submission/common/geoCache";
import { getServerOffsetMs } from "../components/Submission/common/deadline";
import {
  BD_PARAM,
  buildFlowSearch,
  flowRefs,
} from "../components/Submission/common/submissionParams";
import {
  clearPersistedFlow,
  hasPersistedFlow,
  IPersistedFlow,
  readPersistedFlow,
  SubmissionEntrySource,
  writePersistedFlow,
} from "../components/Submission/common/persistedFlow";
import { gateNavigation } from "../components/Submission/common/flowGuard";
import { getBreakdownInfo } from "../components/Submission/common/breakdownInfo";
import { trackSubmissionEvent } from "../components/Submission/analytics";

const FLOW_VERSION = 1;

// The one geo lookup currently in flight, shared by concurrent callers.
let geoInFlight: Promise<ISubmissionGeoGuessDto | null> | null = null;

export interface ISubmissionFlowState {
  flowVersion: number;
  entrySource: SubmissionEntrySource | null;
  // Raw `bd` / `roleid` refs — strings, never parsed (the breakdown ref is zero-padded).
  breakdownId: string | null;
  roleId: string | null;
  submissionInfo: IBreakdownSubmissionInfoDto | null;
  serverOffsetMs: number;
  geoGuess: ISubmissionGeoGuessDto | null;
  // False until the guess has been attempted. `geoGuess: null` alone cannot
  // distinguish "not asked yet" from "asked and the lookup failed", and
  // country-dependent copy must not flash the wrong promise while it resolves.
  geoResolved: boolean;
  currentStep: number;
  startedAtUtc: string | null;
  loading: boolean;
  error: any;
}

const initialState: ISubmissionFlowState = {
  flowVersion: FLOW_VERSION,
  entrySource: null,
  breakdownId: null,
  roleId: null,
  submissionInfo: null,
  serverOffsetMs: 0,
  geoGuess: null,
  geoResolved: false,
  currentStep: 1,
  startedAtUtc: null,
  loading: false,
  error: null,
};

// Storage lives in common/persistedFlow.ts so the entry guard can read it
// without importing this model (and with it, the whole API layer).
const persistFlow = (state: ISubmissionFlowState) => {
  if (!state.entrySource) return;
  writePersistedFlow({
    entrySource: state.entrySource,
    breakdownId: state.breakdownId,
    roleId: state.roleId,
    currentStep: state.currentStep,
    startedAtUtc: state.startedAtUtc,
  });
};

export { clearPersistedFlow, hasPersistedFlow };

// The breakdown can't be submitted to any more: closed, unavailable, every role
// cast (the API answers Closed for each), or a deadline already past.
export const isBreakdownClosed = (info: IBreakdownSubmissionInfoDto): boolean => {
  if (!info) return false;
  if (info.status !== "Open") return true;
  // E8 (docs/16): the status can still say Open after the closing date, until
  // whatever flips it runs. Judged on the server's clock, which the same
  // response carries, so a visitor's wrong clock can't open or close a job.
  // Except a late one: the API keeps it Open on purpose for a few days after
  // the deadline (isLate), and the flow goes on, saying it has expired.
  const closes = Date.parse(info.closingDateUtc);
  const now = Date.parse(info.serverTimeUtc);
  return !info.isLate && !isNaN(closes) && !isNaN(now) && closes <= now;
};

// Gate rule: closed breakdown, a deadline already past, or the targeted role no
// longer open.
export const isFlowBlocked = (
  info: IBreakdownSubmissionInfoDto,
  roleId?: string | null
): boolean => {
  if (!info) return false;
  if (isBreakdownClosed(info)) return true;
  if (roleId && info.selectedRole && info.selectedRole.status !== "Open") return true;
  return false;
};

// At the entry, the link's role was cast or booked while the breakdown still has
// roles open (decided 2026-10-30): the visitor picks another on the landing page,
// which says theirs has been filled. Every role gone is a closed breakdown.
export const isRoleCastAtOpenBreakdown = (
  info: IBreakdownSubmissionInfoDto,
  roleId?: string | null
): boolean =>
  !!info &&
  !!roleId &&
  !!info.selectedRole &&
  info.selectedRole.status !== "Open" &&
  !isBreakdownClosed(info);

// The link's role isn't one of the breakdown's (a bad or stale id): the API
// echoes no selectedRole. Nothing can be sent to it, at any step.
export const isRoleNotFound = (
  info: IBreakdownSubmissionInfoDto,
  roleId?: string | null
): boolean => !!info && !!roleId && !info.selectedRole;

// The role can't be had as the link offered it: it isn't the breakdown's, it
// has been cast, or the breakdown has closed since they started. Only then does
// the breakdown's title link to its full page on the classic site, so they can
// look closer (decided 2026-10-26); otherwise the title is plain text.
export const isRoleUnavailable = (
  info: IBreakdownSubmissionInfoDto | null,
  roleId?: string | null
): boolean =>
  !!info &&
  (isRoleNotFound(info, roleId) ||
    info.status !== "Open" ||
    (!!roleId && !!info.selectedRole && info.selectedRole.status !== "Open"));

// Steps 2 to 4 (decided 2026-10-26): one role cast since they started doesn't
// stop them while the breakdown has others open; the send goes through. A role
// that isn't the breakdown's does (they pick another), and so does a breakdown
// that can't be submitted to any more (the closed page; the server refuses the
// draft, the materials, the hold, the checkout and the send).
export const stopsMidway = (
  info: IBreakdownSubmissionInfoDto,
  roleId?: string | null
): boolean => isRoleNotFound(info, roleId) || isBreakdownClosed(info);

// Where a role that isn't the breakdown's sends the visitor: the breakdown's
// own page, which lists its open roles. The bad roleid stays on the URL so the
// page can say why they're there; everything else on the URL rides along.
export const rolePickerPath = (
  search: string | undefined,
  breakdownId: string,
  roleId?: string | null
) => `/submission/boost${buildFlowSearch(search, breakdownId, roleId)}`;

// One submission per actor per casting call, a system-wide rule (decided
// 2026-10-27): an account already with casting for this breakdown, any role,
// gets the closed page's "already submitted" version, and the flow stops.
export const alreadySubmittedPath = (search: string | undefined, breakdownId: string) => {
  const params = new URLSearchParams(buildFlowSearch(search, breakdownId, null));
  params.set("reason", "submitted");
  return `/submission/closed?${params.toString()}`;
};

const closedPath = (breakdownId: string | null) =>
  breakdownId
    ? `/submission/closed?${BD_PARAM}=${encodeURIComponent(breakdownId)}`
    : "/submission/closed";

const submissionFlowModel = createModel({
  state: initialState,
  reducers: {
    setLoading(state: ISubmissionFlowState, loading: boolean) {
      return { ...state, loading, error: loading ? null : state.error };
    },
    setError(state: ISubmissionFlowState, error: any) {
      return { ...state, error, loading: false };
    },
    setEntry(
      state: ISubmissionFlowState,
      payload: {
        entrySource: SubmissionEntrySource;
        breakdownId: string | null;
        roleId: string | null;
      }
    ) {
      return {
        ...state,
        ...payload,
        // A role-less flow must not inherit the previous (closed) breakdown's
        // snapshot, or the header would advertise "Closed" for the whole signup.
        submissionInfo: payload.breakdownId ? state.submissionInfo : null,
        serverOffsetMs: payload.breakdownId ? state.serverOffsetMs : 0,
        currentStep: 1,
        startedAtUtc: state.startedAtUtc || new Date().toISOString(),
      };
    },
    setSubmissionInfo(
      state: ISubmissionFlowState,
      payload: { info: IBreakdownSubmissionInfoDto; serverOffsetMs: number }
    ) {
      return {
        ...state,
        submissionInfo: payload.info,
        serverOffsetMs: payload.serverOffsetMs,
        loading: false,
        error: null,
      };
    },
    setGeoGuess(state: ISubmissionFlowState, geoGuess: ISubmissionGeoGuessDto | null) {
      return { ...state, geoGuess, geoResolved: true };
    },
    hydrate(state: ISubmissionFlowState, persisted: IPersistedFlow) {
      return {
        ...state,
        entrySource: persisted.entrySource,
        breakdownId: persisted.breakdownId,
        roleId: persisted.roleId,
        currentStep: persisted.currentStep,
        startedAtUtc: persisted.startedAtUtc,
      };
    },
    reset() {
      return { ...initialState };
    },
  },
  effects: (dispatch: any) => ({
    // Entry pages: fetch breakdown context without committing to a flow yet.
    async loadEntry(payload: { breakdownId: string; roleId?: string }) {
      dispatch.submissionFlowModel.setLoading(true);
      try {
        const info = await getBreakdownInfo(payload.breakdownId, payload.roleId);
        dispatch.submissionFlowModel.setSubmissionInfo({
          info,
          serverOffsetMs: getServerOffsetMs(info.serverTimeUtc),
        });
        return info;
      } catch (err) {
        dispatch.submissionFlowModel.setError(err);
        throw err;
      }
    },

    // Role CTA: ALWAYS re-fetches before navigating (gate re-check point 1)
    // so a deadline that passed while the user read the page is caught here.
    async startFlow(payload: {
      breakdownId: string;
      roleId?: string;
      entrySource: SubmissionEntrySource;
      history: any;
      // location.search of the page the user came from, so campaign params
      // ride along with bd/roleid into the wizard.
      search?: string;
    }) {
      const { breakdownId, roleId, entrySource, history, search } = payload;
      // Push or replace when the gate fires, depending on whether the page
      // behind us is worth going back to. See gateNavigation.
      const toGate = (path: string, state?: any) =>
        gateNavigation(entrySource) === "push"
          ? history.push(path, state)
          : history.replace(path, state);
      dispatch.submissionFlowModel.setLoading(true);
      let info: IBreakdownSubmissionInfoDto;
      try {
        info = await getBreakdownInfo(breakdownId, roleId);
      } catch (err) {
        if (err && (err as any).status === 404) {
          // Unknown breakdown behaves like a closed one (generic copy).
          dispatch.submissionFlowModel.setLoading(false);
          toGate("/submission/closed");
          return null;
        }
        dispatch.submissionFlowModel.setError(err);
        throw err;
      }

      const serverOffsetMs = getServerOffsetMs(info.serverTimeUtc);
      dispatch.submissionFlowModel.setSubmissionInfo({ info, serverOffsetMs });

      if (isRoleNotFound(info, roleId) || isRoleCastAtOpenBreakdown(info, roleId)) {
        toGate(rolePickerPath(search, breakdownId, roleId));
        return info;
      }
      if (isFlowBlocked(info, roleId)) {
        // Back has to land somewhere useful. From the browse list that is the
        // list itself, so this pushes; from the boost page, which gates on load,
        // pushing would bounce Back between the two, so it replaces.
        toGate(closedPath(breakdownId), {
          breakdownName: info.breakdownName,
          closedReason: info.closedReason,
        });
        return info;
      }

      dispatch.submissionFlowModel.setEntry({
        entrySource,
        breakdownId,
        roleId: roleId || null,
      });
      dispatch.submissionFlowModel.persist();
      // The funnel's start, and the only place that can honestly claim it: the
      // gate has passed, the entry refs are stored, and the next line is the
      // navigation into step 1. It used to fire from the step-4 shell, which
      // meant it marked the END of the flow — and once step 4 got its own route
      // that shell became unreachable, so it stopped firing at all.
      trackSubmissionEvent("submission_flow_started", {
        breakdownId,
        roleId: roleId || null,
        entrySource,
      });
      // bd/roleid must be on every wizard URL, so a refresh or a shared link
      // still knows what is being submitted to.
      history.push(
        `/submission/flow/step/1${buildFlowSearch(search, breakdownId, roleId)}`
      );
      return info;
    },

    // Wizard-step mounts + rehydrate (gate re-check points 2 and 3).
    // Returns the fresh info; navigates to the closed gate itself if blocked.
    async refreshGate(
      payload: {
        history: any;
        search?: string;
        paid?: boolean;
        // Step 1 only: the visitor hasn't started, so a closed breakdown or a
        // cast role still stops them. Steps 2 to 4 are midway (stopsMidway).
        entry?: boolean;
        // The email code: only a role that isn't the breakdown's stops them, so
        // the account still gets made; step 2 then shows a closed breakdown.
        verify?: boolean;
      },
      rootState: any
    ) {
      let state: ISubmissionFlowState = rootState.submissionFlowModel;
      // Self-healing: a page reload resets the store, so restore the tab's
      // persisted flow here rather than relying on the caller. Without this
      // the gate silently no-ops after a refresh on any step page.
      if (!state.entrySource) {
        const persisted = readPersistedFlow();
        if (persisted) {
          dispatch.submissionFlowModel.hydrate(persisted);
          state = { ...state, ...persisted } as ISubmissionFlowState;
        }
      }
      // The link wins (flowRefs). Cold entry: a shared or hand-typed step URL
      // carries bd/roleid, but the store is empty and sessionStorage has
      // nothing because the boost page was never visited in this tab: adopt
      // the refs from the URL, or the whole wizard renders context-less (no
      // blue header, no deadline gate). And a link for another breakdown than
      // the one this tab remembers (back from the login for another role) is
      // for that one.
      const search =
        payload.search ||
        (typeof window !== "undefined" ? window.location.search : "");
      const refs = flowRefs(state, search);
      if (refs.breakdownId && (refs.breakdownId !== state.breakdownId || refs.roleId !== state.roleId)) {
        const entry = {
          entrySource: state.entrySource || ("direct" as SubmissionEntrySource),
          breakdownId: refs.breakdownId,
          roleId: refs.roleId,
        };
        dispatch.submissionFlowModel.setEntry(entry);
        dispatch.submissionFlowModel.persist();
        state = { ...state, ...entry };
      }
      if (!state.breakdownId) return null;
      try {
        // Kept a minute per breakdown and role (common/breakdownInfo), so each step doesn't
        // ask again; step 4's can already be on its way, started by the phone step's countdown.
        const info = await getBreakdownInfo(state.breakdownId, state.roleId);
        dispatch.submissionFlowModel.setSubmissionInfo({
          info,
          serverOffsetMs: getServerOffsetMs(info.serverTimeUtc),
        });
        if (
          isRoleNotFound(info, state.roleId) ||
          (payload.entry && isRoleCastAtOpenBreakdown(info, state.roleId))
        ) {
          // Replaced, like the closed gate below, so Back doesn't bounce. A cast
          // role only here at step 1: midway it goes on (stopsMidway).
          payload.history.replace(
            rolePickerPath(payload.search, state.breakdownId, state.roleId)
          );
        } else if (
          payload.entry
            ? isFlowBlocked(info, state.roleId)
            : !payload.verify && isBreakdownClosed(info)
        ) {
          // Always replace here, whatever the entry source: this fires on a
          // step's own mount, so Back would return to that step and the step
          // would gate again. The step it replaces was itself pushed, so Back
          // still reaches the page the flow started from.
          payload.history.replace(closedPath(state.breakdownId), {
            breakdownName: info.breakdownName,
            closedReason: info.closedReason,
            // Back from a purchase: the closed page says the plan is kept.
            paid: !!payload.paid,
          });
        }
        return info;
      } catch (err) {
        if (err && (err as any).status === 404) {
          payload.history.replace("/submission/closed");
          return null;
        }
        // Network failure must never masquerade as "breakdown closed" —
        // keep the last snapshot and let the step render a retry state.
        Sentry.captureException(err);
        dispatch.submissionFlowModel.setError(err);
        return null;
      }
    },

    // IP-based location guess: drives the country-dependent copy on Step 1,
    // preselects city/country and feeds the account-creation defaults. Failure
    // is silent (the copy falls back and the user picks the city manually).
    //
    // Read from the app-wide `CountryCode` localStorage variable first (the
    // same one the actor/agent/talent signups use) so the endpoint is hit once
    // per device rather than once per step — and so a lookup done by either
    // funnel serves the other. See common/geoCache.ts.
    async loadGeoGuess() {
      // ?ctry= skips the cache on the way IN — the point of the override is to
      // see a country other than the stored one.
      const forced = getForcedCountryCode();
      if (!forced) {
        const cached = readCachedGeo();
        if (cached) {
          dispatch.submissionFlowModel.setGeoGuess(cached);
          return cached;
        }
      }
      // The step header asks for this on every step, and step 1 asks too, so a
      // cold load can have two callers in the same tick. They share one request
      // rather than racing two lookups for the same answer.
      if (geoInFlight) return geoInFlight;
      geoInFlight = (async () => {
        try {
          const geo = await getSubmissionFlowApi().getRequesterLocation();
          // ...but it DOES write through, so changing the query string brings
          // the stored country up to date instead of leaving the rest of signup
          // on the previous one. Same behaviour as the app's own
          // referencesModel.setForceRequesterCountryCode.
          writeCachedGeo(geo);
          dispatch.submissionFlowModel.setGeoGuess(geo);
          return geo;
        } catch (err) {
          dispatch.submissionFlowModel.setGeoGuess(null);
          return null;
        } finally {
          geoInFlight = null;
        }
      })();
      return geoInFlight;
    },

    // Restore a flow after refresh. Returns true when something was restored;
    // the caller still runs refreshGate to re-validate the deadline.
    async rehydrate() {
      const persisted = readPersistedFlow();
      if (!persisted) return false;
      dispatch.submissionFlowModel.hydrate(persisted);
      return true;
    },

    async persist(_: void, rootState: any) {
      persistFlow(rootState.submissionFlowModel);
    },

    async abandonFlow() {
      clearPersistedFlow();
      dispatch.submissionFlowModel.reset();
    },
  }),
});

export default submissionFlowModel;
